import { Flex, Text } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import { useIntl } from "react-intl";
import { useErrorModal } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import type { SelectFundingFormFields } from "../../components";
import { SelectFundingEntryForm } from "../../components";
import { useFundingApplication, useFundingApplicationMutation } from "../../queries/funding-application/queries";
import { calculateEligibleAmount } from "../../utils/eligible-funding-amount";

import { MESSAGES } from "./message-definitions";

export interface SelectFundingFormProps {
  onContinue: () => void;
  formId: string;
}

export const SelectFundingForm = ({ onContinue, formId }: SelectFundingFormProps) => {
  const intl = useIntl();
  const { openErrorModal } = useErrorModal();

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data, isFetched } = useFundingApplication(customerDomain);
  const selectFundingMutation = useFundingApplicationMutation(customerDomain);
  const { eligibleAmount, eligibleCurrency } = data
    ? calculateEligibleAmount(
        data.reportedRevenue,
        data.reportedRevenueCurrency,
        data.precalculatedIndicativeOfferAmount,
        data.precalculatedIndicativeOfferCurrency
      )
    : { eligibleAmount: 0, eligibleCurrency: "" };

  const onSelectFundingSubmit = async (formData: SelectFundingFormFields) => {
    if (!isFetched || !data) {
      return;
    }
    await selectFundingMutation
      .mutateAsync({
        ...formData,
        requestedAmountCurrency: formData.requestedAmountCurrency
          ? (formData.requestedAmountCurrency as FundingApplicationSchemas.Currency)
          : undefined,
        id: data.id,
      })
      .then(onContinue)
      .catch((error) => {
        openErrorModal({ variant: "submit-error", error });
      });
  };

  return isFetched && data ? (
    <Flex direction="column" gap="6">
      <SelectFundingEntryForm
        onSubmit={onSelectFundingSubmit}
        initialValues={{
          requestedAmount: data?.requestedAmount || eligibleAmount,
          requestedAmountCurrency:
            data?.requestedAmount && data?.requestedAmountCurrency ? data?.requestedAmountCurrency : eligibleCurrency,
          intendedUse: data?.intendedUse || [],
          intendedUseOther: data?.intendedUseOther,
          preferredFundsDeploymentChoice: data?.preferredFundsDeploymentChoice ?? "asap",
          preferredFundsDeploymentDate: data?.preferredFundsDeploymentDate ?? null,
        }}
        formId={formId}
      />
      <Text align="start" size="sm">
        {"*" + intl.formatMessage(MESSAGES.additionalInfo)}
      </Text>
    </Flex>
  ) : null;
};
